import { CustomButtonFields } from '@portal/shared/components/ContentBlocks/shared';
import { cloneDeep, isNull, isUndefined, set } from 'lodash';
import { type PageSection } from '@portal/types/studio/page';
import { customizableFields, type CustomizableMediaProperty } from './customContentConfig';
import type { DynamicSlice, CustomContent, AssetLibrarySection } from './types';

export function isAssetLibrarySection(
  section: PageSection | AssetLibrarySection | undefined
): section is AssetLibrarySection {
  return !!section && `type` in section && section.type === 'asset_library';
}

export function isSanityJobListSection(
  section: PageSection | AssetLibrarySection | undefined
): section is Extract<PageSection, { _type: 'jobList' }> {
  return !!section && `_type` in section && section._type === 'jobList';
}

export const customizeSectionContent = (
  section: NonNullable<DynamicSlice['sections']>[0],
  slug: string,
  customContentsData: CustomContent[]
) => {
  const config = customizableFields[section._type] || {};
  const updatedSection = cloneDeep(section);

  Object.entries(config).forEach(([fieldType, value]) => {
    if (value) {
      let customContentItem: (typeof customContentsData)[number] | undefined;

      if (fieldType === 'media') {
        const mediaValue = value as CustomizableMediaProperty;
        const keyOverride = (value as { [key: string]: { key: string } })[slug] || value;
        const mediaPaths = Array.isArray(mediaValue.path) ? mediaValue.path : [mediaValue.path];

        mediaPaths.forEach((path: string) => {
          // Desktop media
          customContentItem = customContentsData.find(
            (content) =>
              content.section_slug === slug &&
              content.content_type?.includes(`media+${keyOverride.key}+sections[0].${path}`)
          );

          if (customContentItem && customContentItem.content_type) {
            const [, , , assetType] = customContentItem.content_type.split('+');
            const hasMediaType =
              !isNull(mediaValue.mediaTypePath) && !isUndefined(mediaValue.mediaTypePath);

            const assetRefpath = `${path}.${assetType}.asset._ref`;
            set(updatedSection, assetRefpath, customContentItem.content);

            if (hasMediaType && !isUndefined(mediaValue.mediaTypePath)) {
              set(updatedSection, mediaValue.mediaTypePath, assetType);
            }
          }

          // Mobile media
          const mobileContentItem = customContentsData.find(
            (content) =>
              content.section_slug === slug &&
              content.content_type?.includes(`media+${keyOverride.key}+mobile.sections[0].${path}`)
          );

          if (mobileContentItem && mobileContentItem.content_type) {
            const [, , , assetType] = mobileContentItem.content_type.split('+');
            const mobileAssetRefpath = `${path}.mobileImage.asset._ref`;
            set(updatedSection, mobileAssetRefpath, mobileContentItem.content);
            set(updatedSection, `${path}.mediaTypeForMobile`, assetType);
          }

          // Mobile toggle
          const mobileToggleItem = customContentsData.find(
            (content) =>
              content.section_slug === slug &&
              content.content_type === `media+${keyOverride.key}+mobile-toggle`
          );

          if (mobileToggleItem) {
            set(updatedSection, `${path}.withMobileMedia`, mobileToggleItem.content === 'true');
          }
        });
      } else if (fieldType === 'logo') {
        const mediaValue = value as CustomizableMediaProperty;
        customContentItem = customContentsData.find(
          (content) =>
            content.section_slug === slug &&
            content.content_type?.includes(`media+${mediaValue.key}+sections[0].${mediaValue.path}`)
        );

        if (customContentItem) {
          if (slug === 'reasons-why') {
            // special case for hero section HeroText component variant "reasons-why"
            // check custom content for "banner-logo" and see if the logo should be hidden
            const shouldShowLogoItem = customContentsData.find(
              (contentData) =>
                contentData.section_slug === slug && contentData.content_type === 'banner-logo'
            );

            if (shouldShowLogoItem && shouldShowLogoItem.content === 'false') {
              // hide logo if "banner-logo" customContent returns a value equal to "false"
              set(updatedSection, 'logo', null);
            } else {
              // show logo if "banner-logo" customContent returns a value not equal to "false"
              // or "banner-logo" customContent could not be found
              set(updatedSection, `${mediaValue.path}.asset._ref`, customContentItem.content);
            }
          } else {
            set(updatedSection, `${mediaValue.path}.asset._ref`, customContentItem.content);
          }
        }
      } else if (fieldType === 'paddingTop' || fieldType === 'paddingBottom') {
        customContentItem = customContentsData.find(
          (content) => content.section_slug === slug && content.content_type === fieldType
        );
        if (customContentItem && customContentItem.content) {
          set(updatedSection, value as string, customContentItem.content);
        }
      } else if (fieldType === 'custom-button') {
        customContentItem = customContentsData.find(
          (content) => content.section_slug === slug && content.content_type === fieldType
        );
        if (customContentItem && customContentItem.content) {
          const buttonContent = JSON.parse(
            customContentItem.content
          ) as Partial<CustomButtonFields>;

          if (buttonContent.label && buttonContent.link && 'unifiedButton' in updatedSection) {
            updatedSection.unifiedButton = {
              ...updatedSection.unifiedButton,
              _type: 'unifiedButton',
              switchButton: 'link',
              useMobileButtonText: false,
              baseButtonLinkV2: {
                _type: 'baseButtonLinkV2',
                variant: 'primary',
                btnLink: {
                  _type: 'btnLink',
                  external: buttonContent.link
                },
                showIcon: false,
                targetBlank: false,
                portableText: [
                  {
                    _key: '70ec8c3ffab1',
                    markDefs: [],
                    children: [
                      {
                        marks: [],
                        text: buttonContent.label,
                        _key: 'b3d14ea2a79f',
                        _type: 'span'
                      }
                    ],
                    _type: 'block',
                    style: 'normal'
                  }
                ]
              }
            };
          }
        }
      } else if (fieldType === 'isFootNoteEnabled') {
        customContentItem = customContentsData.find(
          (content) => content.section_slug === slug && content.content_type === 'show-footnote'
        );
        if (customContentItem && customContentItem.content) {
          set(updatedSection, value as string, customContentItem.content);
        }
      } else if (fieldType === 'footNoteValue') {
        customContentItem = customContentsData.find(
          (content) => content.section_slug === slug && content.content_type === 'footnote'
        );
        if (customContentItem && customContentItem.content) {
          set(updatedSection, value as string, customContentItem.content);
        }
      } else {
        customContentItem = customContentsData.find(
          (content) => content.section_slug === slug && content.content_type === fieldType
        );
        if (customContentItem && customContentItem.content) {
          set(updatedSection, value as string, customContentItem.content);
        }
      }
    }
  });

  return updatedSection;
};

export const addParamToUrl = (url: string, param: string) =>
  url.includes('?') ? `${url}&${param}` : `${url}?${param}`;

export const DYNAMIC_LANDERS_QUERY = `
  {
    slug,
    _type,
    "sections": sections[] {
      ...,
      _type == "faqV2" => {
        "questions": questions[] {
          ...,
          _type == "faqItemReference" => @->,
        }
      },
      _type in ['heroHalfV5'] => {
        ...,
        additionalLink {
          ...,
          btnLink {
            ...,
            internal -> {
              slug
            }
          }
        },
        additionalLinkMobile {
          ...,
          btnLink {
            ...,
            internal -> {
              slug
            }
          }
        },
        additionalLinkUnifiedOverride {
          ...,
          baseButtonLinkV2 {
            ...,
            btnLink {
              ...,
              internal -> {
                slug
              }
            }
          }
        },
        additionalLinkUnifiedOverrideMobile {
          ...,
          baseButtonLinkV2 {
            ...,
            btnLink {
              ...,
              internal -> {
                slug
              }
            }
          }
        }
      },
      _type == "reviews" => {
        "items": items[] {
          ...,
          _type == "reviewItemReference" => @->,
        }
      }
    }
  }
`;
